/**
 * API base URL. Production'da (astrosocialapp.com) istekler api.astroai.work'e gider; API domaini ayrıdır.
 * Geliştirmede REACT_APP_API_URL ile override (örn. http://localhost:8000).
 */
function getApiBase(): string {
  if (typeof window === 'undefined') return process.env.REACT_APP_API_URL || '';
  const env = process.env.REACT_APP_API_URL;
  if (env) return env;
  const host = window.location.hostname;
  const landingHosts = new Set([
    'astrosocialapp.com',
    'www.astrosocialapp.com',
    'astroai.work',
    'www.astroai.work',
  ]);
  if (landingHosts.has(host)) {
    return `${window.location.protocol}//api.astroai.work`;
  }
  if (host === 'localhost' || host === '127.0.0.1') return '';
  return '';
}
const API_BASE = getApiBase();

export interface LoginRequest {
  email: string;
  password: string;
  turnstile_token?: string;
}

export interface CaptchaTurnstileConfig {
  site_key: string;
  required: boolean;
}

export interface CaptchaConfig {
  turnstile: CaptchaTurnstileConfig | null;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
}

export interface ProfileResponse {
  id: number;
  email: string;
  name: string;
  created_at?: string;
}

export interface UsageDetail {
  used: number;
  limit: number;
  remaining: number;
}

export interface UsageStats {
  monthly_astro_charts: UsageDetail;
  daily_tarot_readings: UsageDetail;
}

export interface Limits {
  monthly_astro_charts: number;
  daily_tarot_readings: number;
  max_saved_readings: number;
  max_astro_persons: number;
}

export interface Subscription {
  tier: 'free' | 'plus';
  status: string;
  expires_at?: string;
  is_active: boolean;
}

export interface SubscriptionStatusResponse {
  subscription: Subscription;
  limits: Limits;
  usage: UsageStats;
  subscription_enabled: boolean;
}

function getStoredToken(): string | null {
  return localStorage.getItem('access_token');
}

function setStoredToken(token: string | null): void {
  if (token) localStorage.setItem('access_token', token);
  else localStorage.removeItem('access_token');
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getStoredToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 401) {
    setStoredToken(null);
    throw new Error('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.json() as Promise<T>;
}

/** Turnstile site key (herkese açık, giriş gerekmez). */
export async function getCaptchaConfig(): Promise<CaptchaConfig> {
  const base = API_BASE || '';
  const res = await fetch(`${base}/api/auth/captcha-config`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    return { turnstile: null };
  }
  return res.json() as Promise<CaptchaConfig>;
}

export async function login(data: LoginRequest): Promise<LoginResponse> {
  const body: Record<string, string> = {
    email: data.email,
    password: data.password,
  };
  if (data.turnstile_token && data.turnstile_token.trim()) {
    body.turnstile_token = data.turnstile_token.trim();
  }
  const res = await apiFetch<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  setStoredToken(res.access_token);
  return res;
}

export function logout(): void {
  setStoredToken(null);
}

export function getStoredAccessToken(): string | null {
  return getStoredToken();
}

export async function getProfile(): Promise<ProfileResponse> {
  return apiFetch<ProfileResponse>('/api/auth/me');
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  return apiFetch<SubscriptionStatusResponse>('/api/subscription/status');
}

export interface ResetPasswordVerifyResponse {
  valid: boolean;
  email: string;
}

/** Şifre sıfırlama token’ı (giriş gerekmez). */
export async function verifyResetPasswordToken(token: string): Promise<ResetPasswordVerifyResponse> {
  const base = API_BASE || '';
  const q = new URLSearchParams({ token });
  const res = await fetch(`${base}/api/auth/reset-password/verify?${q}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.json() as Promise<ResetPasswordVerifyResponse>;
}

/** Yeni şifreyi kaydet (giriş gerekmez). */
export async function submitPasswordReset(
  token: string,
  newPassword: string
): Promise<{ message: string }> {
  const base = API_BASE || '';
  const res = await fetch(`${base}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.json() as Promise<{ message: string }>;
}

export type LegalMarkdownPath =
  | 'privacy-policy'
  | 'terms-of-service'
  | 'child-safety-standards';

export async function getLegalMarkdown(
  path: LegalMarkdownPath,
  options?: { lang?: 'tr' | 'en' },
): Promise<string> {
  const base = API_BASE || '';
  const langQ =
    path === 'child-safety-standards' && options?.lang === 'tr'
      ? '?lang=tr'
      : '';
  const res = await fetch(`${base}/api/legal/${path}${langQ}`);
  if (!res.ok) throw new Error('Metin yüklenemedi');
  const text = await res.text();
  const trimmed = (text || '').trim();
  // API bazen HTML hata sayfası dönebilir; markdown değilse fallback için boş say
  if (trimmed.startsWith('<') || trimmed.startsWith('<!')) return '';
  return trimmed;
}
